import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const container = document.getElementById("root");

const tree = (
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>
);

/**
 * Three mount paths.
 *
 * 1. Snapshot pass. `scripts/prerender.mjs` sets `window.__PRERENDER_SNAPSHOT__`
 *    in headless Chromium before the bundle runs. We then render the tree with
 *    `renderToString` and write the result into #root by hand.
 *
 *    Rendering to a string rather than mounting matters. React's `useId` emits
 *    one format when it renders on the client (`:r0:`) and another when it
 *    hydrates (`:R2d:`). Radix builds every dialog's `aria-controls` from
 *    `useId`, so a snapshot taken from a client-rendered DOM can never hydrate
 *    cleanly. `renderToString` produces the hydration format, so the served
 *    markup and the visitor's first render agree exactly.
 *
 *    The import is dynamic, so `react-dom/server` lands in its own chunk that
 *    production visitors never download.
 *
 * 2. Hydration. A prerendered route arrives with real markup in #root, so the
 *    visible page is attached to rather than blanked and rebuilt.
 *
 * 3. Plain client render. The dev server and the authenticated app mount into
 *    an empty container exactly as before.
 */
if (window.__PRERENDER_SNAPSHOT__) {
  import("react-dom/server")
    .then(({ renderToString }) => {
      container.innerHTML = renderToString(tree);
      window.__PRERENDER_READY__ = true;
    })
    .catch((error) => {
      window.__PRERENDER_ERROR__ = String(error);
    });
} else if (container.hasChildNodes()) {
  ReactDOM.hydrateRoot(container, tree);
} else {
  ReactDOM.createRoot(container).render(tree);
}
